import { cn } from "@acme/ui";
import { buttonVariants } from "@acme/ui/button";
import { Card } from "@acme/ui/card";
import { Skeleton } from "@acme/ui/skeleton";
import { ErrorBoundary } from "@sentry/tanstackstart-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { Suspense } from "react";
import { DataTable } from "~/components/data-table/data-table";
import { experienceColumns } from "~/components/experiences/columns";
import { queryKeys } from "~/lib/query-keys";
import { $getAllExperiences } from "~/lib/server/experience";

export const Route = createFileRoute("/(dashboard)/experiences/")({
  component: Experiences,
  loader: async ({ context: { queryClient } }) =>
    await queryClient.prefetchQuery({
      queryKey: queryKeys.experience.listAll(),
      queryFn: () => $getAllExperiences(),
    }),
  head: () => ({
    meta: [
      { title: "Experiences | Dashboard" },
      { name: "description", content: "Manage your portfolio experiences" },
    ],
  }),
});

function ExperiencesLoading() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-8 w-[250px]" />
      <Skeleton className="h-4 w-[350px]" />
      <div className="mt-6">
        <Skeleton className="h-[400px] w-full rounded-md" />
      </div>
    </div>
  );
}

function ExperiencesError() {
  return (
    <Card className="p-6">
      <div className="text-center">
        <h3 className="font-medium text-destructive text-lg">
          Failed to load experiences
        </h3>
        <p className="mt-1 text-muted-foreground text-sm">
          Please try again later.
        </p>
      </div>
    </Card>
  );
}

function ExperiencesContent() {
  const {
    data: experiences,
    error,
    isLoading,
    isFetching,
  } = useSuspenseQuery({
    queryKey: queryKeys.experience.listAll(),
    queryFn: () => $getAllExperiences(),
  });

  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center">
          <h3 className="font-medium text-destructive text-lg">
            Failed to load experiences
          </h3>
          <p className="mt-1 text-muted-foreground text-sm">
            {error.message ?? "Please try again later."}
          </p>
        </div>
      </Card>
    );
  }

  if (isLoading || isFetching) {
    return <ExperiencesLoading />;
  }

  return (
    <DataTable
      columns={experienceColumns}
      data={experiences}
      entityName="experiences"
    />
  );
}

function Experiences() {
  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-bold text-2xl tracking-tight">Experience List</h2>
          <p className="text-muted-foreground">Manage your experiences here.</p>
        </div>
        <Link
          aria-label="Add new experience"
          className={cn(buttonVariants({ variant: "default" }), "group")}
          to="/experiences/create"
        >
          <span>Add Experience</span> <Plus className="ml-1" size={18} />
        </Link>
      </div>
      <ErrorBoundary fallback={<ExperiencesError />}>
        <Suspense fallback={<ExperiencesLoading />}>
          <ExperiencesContent />
        </Suspense>
      </ErrorBoundary>
    </>
  );
}
